import {
  BadgeCheck,
  BarChart3, // Wallet — tạm ẩn cùng mục menu "KPI & lương nhân viên" bị comment bên dưới, sẽ bật lại khi cần.
  Boxes,
  Columns3,
  Cpu,
  Landmark,
  LayoutDashboard,
  Puzzle,
  Receipt,
  Settings,
  ShieldCheck,
  Tag,
  UserCog,
  Users,
  Warehouse,
} from "lucide-react";

import type { TranslationKey } from "@/lib/i18n/dictionaries";

export interface NavLink {
  type: "link";
  labelKey: TranslationKey;
  to: string;
  icon: typeof LayoutDashboard;
  /** Permission-catalog "module.item" key (vd "warehouse.devices") — visible only when the
   * user's permission groups grant `${permissionKey}.view`, checked via NavContent's
   * `hasPermission`. This is the SAME grant that gates the actual page/API server-side
   * (`@RequirePermissions('${permissionKey}.view')`), so the sidebar now shows exactly the set
   * of pages a user can actually open — it used to be hardcoded by `role` (SUPER_ADMIN/STAFF)
   * instead, which ignored whatever a permission group actually granted a STAFF account. */
  permissionKey: string;
}

export interface NavGroup {
  type: "group";
  labelKey: TranslationKey;
  icon: typeof LayoutDashboard;
  children: NavLink[];
}

export type NavEntry = NavLink | NavGroup;

/**
 * Single source of truth for sidebar navigation. Mirrors the legacy
 * ghz.fe SiderMenu structure (KHO HÀNG / GIAO DỊCH / KHÁCH HÀNG /
 * TÀI CHÍNH KẾ TOÁN / CẤU HÌNH HỆ THỐNG), translated through i18n keys.
 * "Giao dịch" and "Khách hàng" are grouped together in the UI (they're
 * heavily cross-referenced — creating a sale/repair picks a client) even
 * though they stay separate feature folders in code. "Nhân sự" (staff/HR)
 * lives under Tài chính kế toán since payroll and headcount are managed
 * together.
 */
export const navEntries: NavEntry[] = [
  {
    type: "link",
    labelKey: "nav.dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
    permissionKey: "overview.dashboard",
  },
  {
    type: "group",
    labelKey: "nav.warehouseGroup",
    icon: Warehouse,
    children: [
      {
        type: "link",
        labelKey: "nav.products",
        to: "/warehouse-management",
        icon: Cpu,
        permissionKey: "warehouse.devices",
      },
      {
        type: "link",
        labelKey: "nav.accessories",
        to: "/accessories-management",
        icon: Puzzle,
        permissionKey: "warehouse.accessories",
      },
      {
        type: "link",
        labelKey: "nav.warrantyPackages",
        to: "/warranty-packages-management",
        icon: BadgeCheck,
        permissionKey: "warehouse.warranty-packages",
      },
    ],
  },
  {
    type: "group",
    labelKey: "nav.transactionsGroup",
    icon: Receipt,
    children: [
      {
        type: "link",
        labelKey: "nav.transactions",
        to: "/transaction-management",
        icon: Receipt,
        permissionKey: "transactions.transactions",
      },
      {
        type: "link",
        labelKey: "nav.clients",
        to: "/client-management",
        icon: Users,
        permissionKey: "transactions.clients",
      },
    ],
  },
  {
    type: "group",
    labelKey: "nav.finance",
    icon: Landmark,
    children: [
      {
        type: "link",
        labelKey: "nav.statisticsOverview",
        to: "/finance/overview",
        icon: BarChart3,
        permissionKey: "finance.store-overview",
      },
      // Tạm ẩn — chưa phát triển xong, sẽ bật lại sau.
      // {
      //   type: "link",
      //   labelKey: "nav.staffPerformance",
      //   to: "/finance/staff-performance",
      //   icon: Wallet,
      //   permissionKey: "finance.kpi-salary",
      // },
      {
        type: "link",
        labelKey: "nav.inventoryDebt",
        to: "/finance/inventory-debt",
        icon: Boxes,
        permissionKey: "finance.inventory-debt",
      },
      {
        type: "link",
        labelKey: "nav.staff",
        to: "/admin-management",
        icon: UserCog,
        permissionKey: "finance.hr",
      },
    ],
  },
  {
    type: "group",
    labelKey: "nav.system",
    icon: Settings,
    children: [
      {
        type: "link",
        labelKey: "nav.configMetadata",
        to: "/settings/metadata",
        icon: Tag,
        permissionKey: "system.metadata",
      },
      {
        type: "link",
        labelKey: "nav.permissionGroups",
        to: "/settings/permission-groups",
        icon: ShieldCheck,
        permissionKey: "system.permissions",
      },
      {
        type: "link",
        labelKey: "nav.tableColumns",
        to: "/settings/table-columns",
        icon: Columns3,
        // Không có catalog item riêng — "Quản lý xem bảng" là 1 phần của tính năng Phân quyền
        // (thứ tự cột = field-visibility, quản lý chung màn Permission Groups), nên dùng chung
        // permission "system.permissions".
        permissionKey: "system.permissions",
      },
    ],
  },
];
